import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/approval-requests';

const STATUS_TABS = [
  { key: 'PENDING_CHIEF', label: 'Bekleyenler' },
  { key: 'PENDING_HQ', label: 'HQ Onayında' },
  { key: 'APPROVED', label: 'Onaylanan' },
  { key: 'REJECTED', label: 'Reddedilen' },
  { key: '', label: 'Tümü' },
];

const REQUEST_TYPE_LABELS = {
  NON_ROUTE_VISIT: 'Rota Dışı Ziyaret',
  POINT_OPENING: 'Yeni Nokta Açılışı',
};

const STATUS_LABELS = {
  PENDING_CHIEF: { label: 'Şef Onayı Bekliyor', tone: 'warn' },
  PENDING_HQ: { label: 'HQ Onayı Bekliyor', tone: 'warn' },
  APPROVED: { label: 'Onaylandı', tone: 'ok' },
  REJECTED: { label: 'Reddedildi', tone: 'bad' },
  DRAFT: { label: 'Taslak', tone: 'neutral' },
};

const PAGE_SIZE_DEFAULT = 20;

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  // ApiResponse<object>.SuccessResult({ items, pendingCount }) — Newtonsoft default = PascalCase outer.
  const data = pick(payload, 'Data', 'data') ?? payload;
  const items = pick(data, 'items', 'Items') ?? [];
  const pendingCount = pick(data, 'pendingCount', 'PendingCount') ?? 0;
  return { items, pendingCount };
}

function statusPillFor(status) {
  const meta = STATUS_LABELS[status] || { label: status || '-', tone: 'neutral' };
  const tones = {
    ok: { color: '#0f5132', background: 'rgba(0, 200, 117, 0.12)', borderColor: 'rgba(0, 200, 117, 0.30)' },
    warn: { color: '#92400e', background: 'rgba(253, 171, 61, 0.14)', borderColor: 'rgba(253, 171, 61, 0.32)' },
    bad: { color: '#7f1d1d', background: 'rgba(226, 68, 92, 0.12)', borderColor: 'rgba(226, 68, 92, 0.30)' },
    neutral: { color: '#64748b', background: 'rgba(100, 116, 139, 0.14)', borderColor: 'rgba(100, 116, 139, 0.28)' },
  };
  const t = tones[meta.tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      border: `1px solid ${t.borderColor}`, background: t.background, color: t.color,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor', opacity: 0.85 }} />
      {meta.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

function tryPrettyJson(value) {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return String(value);
  }
}

export function ApprovalRequestsPageLegacy() {
  const [statusFilter, setStatusFilter] = useState('PENDING_CHIEF');
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('CreatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionBusyId, setActionBusyId] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Boş statusFilter "Tümü" demek; backend default "PENDING_CHIEF"in devreye girmemesi için
      // boş bir status= query'si gönderiyoruz (IsNullOrWhiteSpace → tüm kayıtlar).
      const url = `${API_BASE}?status=${encodeURIComponent(statusFilter)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const { items, pendingCount: pc } = unwrapResponse(payload);
      setRows(Array.isArray(items) ? items : []);
      setPendingCount(pc);
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'trackingNo', 'TrackingNo'),
        pick(row, 'requestType', 'RequestType'),
        pick(row, 'status', 'Status'),
        pick(row, 'notes', 'Notes'),
        pick(row, 'rejectionReason', 'RejectionReason'),
        pick(row, 'payloadJson', 'PayloadJson'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };

    const filtered = rows.filter(match);

    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'TrackingNo': return pick(row, 'trackingNo', 'TrackingNo') || '';
        case 'RequestType': return pick(row, 'requestType', 'RequestType') || '';
        case 'Status': return pick(row, 'status', 'Status') || '';
        case 'CreatedDate':
        default: {
          const v = pick(row, 'createdDate', 'CreatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };

    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const handleApprove = async (row) => {
    const id = pick(row, 'id', 'Id');
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/approve`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Onay başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Talep onaylandı');
      await load();
    } catch (err) {
      showToast('error', err.message || 'Onay başarısız');
    } finally {
      setActionBusyId(null);
    }
  };

  const openReject = (row) => {
    setRejectRow(row);
    setRejectReason('');
  };

  const submitReject = async () => {
    if (!rejectRow) return;
    const id = pick(rejectRow, 'id', 'Id');
    if (!id) return;
    setActionBusyId(id);
    try {
      const res = await fetch(`${API_BASE}/${id}/reject`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reason: rejectReason.trim() || null }),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Red başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Talep reddedildi');
      setRejectRow(null);
      setRejectReason('');
      await load();
    } catch (err) {
      showToast('error', err.message || 'Red başarısız');
    } finally {
      setActionBusyId(null);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'trackingNo',
      headerName: 'Takip No',
      flex: 0.9,
      minWidth: 130,
      valueGetter: ({ row }) => pick(row, 'trackingNo', 'TrackingNo') || '-',
    },
    {
      field: 'requestType',
      headerName: 'Talep Tipi',
      flex: 1,
      minWidth: 170,
      valueGetter: ({ row }) => {
        const t = pick(row, 'requestType', 'RequestType');
        return REQUEST_TYPE_LABELS[t] || t || '-';
      },
    },
    {
      field: 'status',
      headerName: 'Durum',
      flex: 0.9,
      minWidth: 170,
      sortable: true,
      renderCell: ({ row }) => statusPillFor(pick(row, 'status', 'Status')),
    },
    {
      field: 'createdDate',
      headerName: 'Tarih',
      flex: 0.9,
      minWidth: 150,
      valueGetter: ({ row }) => formatDate(pick(row, 'createdDate', 'CreatedDate')),
    },
    {
      field: 'notes',
      headerName: 'Notlar',
      flex: 1.4,
      minWidth: 200,
      sortable: false,
      valueGetter: ({ row }) => pick(row, 'notes', 'Notes') || '-',
    },
    {
      field: 'gps',
      headerName: 'GPS',
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => {
        const lat = pick(row, 'gpsLatitude', 'GpsLatitude');
        const lng = pick(row, 'gpsLongitude', 'GpsLongitude');
        if (lat == null || lng == null) return <span style={{ color: '#94a3b8' }}>-</span>;
        return (
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            Haritada Aç
          </a>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 1.4,
      minWidth: 280,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const status = pick(row, 'status', 'Status');
        const id = pick(row, 'id', 'Id');
        const busy = actionBusyId === id;
        const canDecide = status === 'PENDING_CHIEF';
        return (
          <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              style={{ ...gridActionButton, background: 'var(--accent)' }}
              onClick={() => setDetailRow(row)}
            >
              Detay
            </button>
            {canDecide && (
              <>
                <button
                  type="button"
                  style={{ ...gridActionButton, background: '#16a34a', opacity: busy ? 0.6 : 1 }}
                  disabled={busy}
                  onClick={() => handleApprove(row)}
                >
                  {busy ? '...' : 'Onayla'}
                </button>
                <button
                  type="button"
                  style={{ ...gridActionButton, background: '#dc2626', opacity: busy ? 0.6 : 1 }}
                  disabled={busy}
                  onClick={() => openReject(row)}
                >
                  Reddet
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ], [actionBusyId]);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Onay Talepleri</h1>
          <p style={s.pageSubtitle}>
            Saha temsilcilerinden gelen rota dışı ziyaret ve yeni nokta açılış talepleri.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 14px', borderRadius: 999,
            background: 'rgba(253, 171, 61, 0.14)',
            border: '1px solid rgba(253, 171, 61, 0.32)',
            color: '#92400e', fontWeight: 800, fontSize: 13,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--warn)' }} />
            {pendingCount} bekleyen
          </span>
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {STATUS_TABS.map(tab => {
            const active = statusFilter === tab.key;
            return (
              <button
                key={tab.key || 'all'}
                type="button"
                onClick={() => setStatusFilter(tab.key)}
                style={{
                  ...(active ? s.btnPrimary : s.btnSecondary),
                  padding: '6px 14px',
                  fontSize: 13,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Takip no / not / payload..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={(row) => setDetailRow(row)}
        exportFileName="onay-talepleri"
      />

      {detailRow && (
        <div style={s.overlay} onClick={() => setDetailRow(null)}>
          <div style={{ ...s.modal, maxWidth: 680 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {pick(detailRow, 'trackingNo', 'TrackingNo') || 'Talep Detayı'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={() => setDetailRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Talep Tipi</label>
                  <div style={s.inputView}>
                    {REQUEST_TYPE_LABELS[pick(detailRow, 'requestType', 'RequestType')] || pick(detailRow, 'requestType', 'RequestType') || '-'}
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{statusPillFor(pick(detailRow, 'status', 'Status'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Oluşturma</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'createdDate', 'CreatedDate'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Karar Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'decidedAt', 'DecidedAt'))}</div>
                </div>
              </div>
              {pick(detailRow, 'notes', 'Notes') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Notlar</label>
                  <div style={s.textareaView}>{pick(detailRow, 'notes', 'Notes')}</div>
                </div>
              )}
              {pick(detailRow, 'rejectionReason', 'RejectionReason') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Red Gerekçesi</label>
                  <div style={{ ...s.textareaView, background: 'rgba(226, 68, 92, 0.06)' }}>
                    {pick(detailRow, 'rejectionReason', 'RejectionReason')}
                  </div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Payload (JSON)</label>
                <pre style={{
                  margin: 0, padding: 12, borderRadius: 10,
                  background: '#0f172a', color: '#e2e8f0',
                  fontSize: 12, lineHeight: 1.5,
                  maxHeight: 280, overflow: 'auto',
                }}>
                  {tryPrettyJson(pick(detailRow, 'payloadJson', 'PayloadJson'))}
                </pre>
              </div>
            </div>
            <div style={s.modalFooter}>
              {pick(detailRow, 'status', 'Status') === 'PENDING_CHIEF' && (
                <>
                  <button
                    type="button"
                    style={{ ...s.btnDanger }}
                    onClick={() => { const r = detailRow; setDetailRow(null); openReject(r); }}
                  >
                    Reddet
                  </button>
                  <button
                    type="button"
                    style={{ ...s.btnPrimary, background: '#16a34a' }}
                    onClick={() => { const r = detailRow; setDetailRow(null); handleApprove(r); }}
                  >
                    Onayla
                  </button>
                </>
              )}
              <button type="button" style={s.btnSecondary} onClick={() => setDetailRow(null)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectRow && (
        <div style={s.overlay} onClick={() => setRejectRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Talebi Reddet</h2>
              <button type="button" style={s.closeBtn} onClick={() => setRejectRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 13, color: '#4b5563' }}>
                <strong>{pick(rejectRow, 'trackingNo', 'TrackingNo') || 'Talep'}</strong> reddedilecek.
                İsterseniz bir gerekçe ekleyebilirsiniz.
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Red Gerekçesi (opsiyonel)</label>
                <textarea
                  rows={4}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Örn. Konum belgelenmemiş, ziyaret amacı net değil…"
                  style={s.textarea}
                />
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setRejectRow(null)}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: actionBusyId ? 0.6 : 1 }}
                disabled={!!actionBusyId}
                onClick={submitReject}
              >
                {actionBusyId ? 'Gönderiliyor...' : 'Reddet'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default ApprovalRequestsPageLegacy;
